import useTheme from "../../../../common/hooks/useTheme";
import { useState } from "react";
import { MetricType, organConnectionsWithMetric } from "../../store/helper";
import classes from "./DataPointOrgan.module.css";
import { platformActions, RootStatePlatform } from "../../store/platform-slice";
import { useDispatch, useSelector } from "react-redux";
import {
  isOppositeGenderSystem,
  useClientGender,
} from "../../hooks/useClientGender";
import { useScoreSidePanel } from "./ScoreSidePanelContext";

type OrganConnectionsKey = keyof typeof organConnectionsWithMetric;

const DataPointOrgan = ({
  id,
  top = "50%",
  left,
  position,
  visible = true,
}: {
  id: number;
  left?: string;
  top?: string;
  position?: "left" | "fix";
  /**
   * When false, the dot is rendered transparently and non-interactive, but
   * its DOM element (and `id={MetricType[id]}` anchor) is preserved so any
   * Xarrow / connector overlay that targets it keeps working. Defaults to
   * true (existing score-screen behavior).
   */
  visible?: boolean;
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [label, setLabel] = useState<string>("");
  const dispatch = useDispatch();
  const { closePanel } = useScoreSidePanel();
  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );

  const selectedActiveConnection = useSelector(
    (state: RootStatePlatform) => state.platform.selectedActiveConnection
  );
  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.score
  );

  const gender = useClientGender();

  const activeConnection =
    organConnectionsWithMetric[selectedSystem as OrganConnectionsKey];

  const isSelected = selectedSystem === id;
  const isSelectedActiveConnection = selectedActiveConnection === id;

  // Gender-based dots genuinely shouldn't render — they represent
  // sex-specific organs and the alternate ID handles the opposite gender.
  if (isOppositeGenderSystem(id, gender)) return null;

  // Invisible-but-present branch: render the same outer div (and its
  // `id={MetricType[id]}` anchor) but fully transparent and inert. This
  // keeps DOM-id-based overlays (Xarrow, etc.) working in contexts like
  // the consultation Body Composition view where the figure is purely
  // decorative and the dots themselves should not be visible/clickable.
  if (!visible) {
    const positionStyle =
      position === "fix" ? { top, left } : { top, left: 0 };
    return (
      <div
        id={MetricType[id]}
        className={classes.dataPoint}
        style={{
          backgroundColor: "transparent",
          borderColor: "transparent",
          pointerEvents: "none",
          ...positionStyle,
        }}
      >
        <div
          className={classes.dataPointInner}
          style={{
            backgroundColor: "transparent",
            borderColor: "transparent",
          }}
        />
      </div>
    );
  }

  if (
    selectedSystem > 0 &&
    activeConnection.indexOf(id as never) === -1 &&
    !isSelected
  ) {
    return null;
  }

  const handleMouseEnter = () => {
    setLabel(reportData.systems.find((item) => item.system === id)?.name || "");
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const positionOfDataPoint =
    position === "fix" ? { top: top, left: left } : { top: top, left: 0 };

  const handleSelection = (id: number) => {
    if (selectedSystem === 0) {
      dispatch(platformActions.setSelectedSystem(id));
      dispatch(platformActions.setSelectedActiveConnection(0));
      dispatch(platformActions.setConnectionTextExpanded(false));
      return;
    }
    if (selectedSystem === id) {
      dispatch(platformActions.setSelectedSystem(0));
      dispatch(platformActions.setSelectedActiveConnection(0));
      dispatch(platformActions.setConnectionTextExpanded(false));
      closePanel();
      return;
    } else {
      dispatch(platformActions.setSelectedActiveConnection(id));
      dispatch(platformActions.setConnectionTextExpanded(false));
    }
  };

  return (
    <div
      id={MetricType[id]}
      className={classes.dataPoint}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => handleSelection(id)}
      style={{
        backgroundColor:
          isSelected || isSelectedActiveConnection
            ? theme.colors_ds.NEUTRAL_BG_MAIN
            : "transparent",
        borderColor:
          isSelected || isSelectedActiveConnection
            ? theme.colors_ds.NEUTRAL_TEXT_DEFAULT
            : "transparent",
        ...positionOfDataPoint,
      }}
    >
      <div
        className={classes.dataPointInner}
        style={{
          backgroundColor:
            isSelected || isSelectedActiveConnection
              ? theme.colors_ds.NEUTRAL_TEXT_DEFAULT
              : theme.colors_ds.NEUTRAL_BG_MAIN,
          borderColor:
            isSelected || isSelectedActiveConnection
              ? theme.colors_ds.NEUTRAL_TEXT_DEFAULT
              : isHovered
              ? theme.colors_ds.PRIMARY_GRAPH_MAIN
              : theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
          borderWidth: isHovered ? "3px" : "1px",
        }}
      >
        {isHovered && (
          <div
            className={classes.descriptionPanelContainer}
            style={{
              borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
              color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
              zIndex: 200,
            }}
          >
            {label}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataPointOrgan;
